import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Key, Copy, CheckCircle, Mail, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecuritySettings = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Email verification
  const [emailVerified, setEmailVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  
  // Phone verification
  const [phone, setPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');

  useEffect(() => {
    // Load user's security settings
    if (user) {
      setTwoFactorEnabled(user.two_factor_enabled || false);
      setPhone(user.phone || '');
      setPhoneVerified(user.phone_verified || false);
      setEmailVerified(user.is_email_verified || false);
    }
  }, [user]);

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      // Note: resend-verification is a public endpoint that only requires email
      // No auth cookies or CSRF token needed
      await axios.post(
        `${API_URL}/api/auth/resend-verification`,
        { email: user.email }
      );
      
      toast({
        title: 'Email Sent!',
        description: 'Check your inbox for the verification link.',
      });
    } catch (error) {
      // Handle rate limit and other errors safely
      const errorData = error.response?.data;
      let errorMessage = 'Could not send verification email. Please try again.';
      
      if (error.response?.status === 429) {
        const retryAfter = errorData?.retry_after_seconds || 300;
        errorMessage = `Too many requests. Please try again in ${Math.ceil(retryAfter / 60)} minute(s).`;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.detail) {
        errorMessage = errorData.detail;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      toast({
        variant: 'destructive',
        title: 'Failed to Send',
        description: errorMessage,
      });
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setBackupCodes(response.data.backup_codes);
      setShowSetup(true);
      
      toast({
        title: 'Setup Started',
        description: 'Scan the QR code with your authenticator app',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Setup Failed',
        description: error.response?.data?.detail || 'Failed to setup 2FA',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!verificationCode) {
      toast({
        variant: 'destructive',
        title: 'Code Required',
        description: 'Please enter the verification code from your app',
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/2fa/enable`,
        { token: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTwoFactorEnabled(true);
      setShowSetup(false);
      setVerificationCode('');
      
      toast({
        title: 'Success!',
        description: '2FA has been enabled on your account',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Verification Failed',
        description: error.response?.data?.detail || 'Invalid code',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!verificationCode) {
      toast({
        variant: 'destructive',
        title: 'Code Required',
        description: 'Please enter your 2FA code to disable',
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/2fa/disable`,
        { token: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTwoFactorEnabled(false);
      setVerificationCode('');
      
      toast({
        title: 'Disabled',
        description: '2FA has been disabled on your account',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed',
        description: error.response?.data?.detail || 'Failed to disable 2FA',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPhoneVerification = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/phone/request-verification`, { phone });
      
      setShowPhoneVerification(true);
      
      // Show dev code if available
      if (response.data.dev_mode && response.data.verification_code) {
        toast({
          title: 'Code Sent (Dev Mode)',
          description: `Your verification code is: ${response.data.verification_code}`,
          duration: 10000,
        });
      } else {
        toast({
          title: 'Code Sent',
          description: `Verification code sent to ${phone}`,
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed',
        description: error.response?.data?.detail || 'Failed to send code',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/phone/verify`,
        { phone, code: phoneCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPhoneVerified(true);
      setShowPhoneVerification(false);
      setPhoneCode('');
      
      toast({
        title: 'Verified!',
        description: 'Phone number has been verified',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Verification Failed',
        description: error.response?.data?.detail || 'Invalid code',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: 'Code copied to clipboard',
    });
  };

  return (
    <div className="space-y-6" role="region" aria-labelledby="security-heading">
      <div>
        <h2 id="security-heading" className="text-2xl font-bold text-white mb-2">Security Settings</h2>
        <p className="text-gray-400">Protect your account with additional security measures</p>
      </div>

      {/* Email Verification Status */}
      <Card className="bg-gray-800 border-gray-700" role="region" aria-labelledby="email-verification-heading">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div 
                className={`w-12 h-12 rounded-lg flex items-center justify-center ${emailVerified ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}
                aria-hidden="true"
              >
                {emailVerified ? (
                  <CheckCircle className="w-6 h-6 text-green-400" />
                ) : (
                  <Mail className="w-6 h-6 text-yellow-400" />
                )}
              </div>
              <div>
                <h3 id="email-verification-heading" className="text-white font-semibold">Email Verification</h3>
                <p className="text-gray-400 text-sm">{user?.email}</p>
              </div>
            </div>
            {emailVerified ? (
              <span 
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400"
                role="status"
                aria-label="Email verification status: Verified"
              >
                <CheckCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                Verified
              </span>
            ) : (
              <span 
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400"
                role="status"
                aria-label="Email verification status: Not verified"
              >
                <AlertCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                Not Verified
              </span>
            )}
          </div>

          {!emailVerified && (
            <div 
              className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-yellow-300 mb-3">
                Verify your email to unlock all features and receive important account notifications.
              </p>
              <Button
                onClick={handleResendVerification}
                disabled={resendingEmail}
                className="bg-yellow-600 hover:bg-yellow-700 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2"
                aria-label={resendingEmail ? 'Sending verification email...' : 'Resend verification email'}
                aria-busy={resendingEmail}
              >
                {resendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" aria-hidden="true" />
                    <span>Resend Verification Email</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="bg-gray-800 border-gray-700" role="region" aria-labelledby="2fa-heading">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Two-Factor Authentication (2FA)</h3>
                <p className="text-gray-400 text-sm">Add an extra layer of security to your account</p>
              </div>
            </div>
            <Switch
              checked={twoFactorEnabled}
              onCheckedChange={(checked) => {
                if (checked && !twoFactorEnabled) {
                  handleSetup2FA();
                }
              }}
            />
          </div>

          {!twoFactorEnabled && !showSetup && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-300 mb-3">
                Enable 2FA to secure your account with time-based codes from an authenticator app like Google Authenticator or Authy.
              </p>
              <Button
                onClick={handleSetup2FA}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Enable 2FA
              </Button>
            </div>
          )}

          {showSetup && (
            <div className="space-y-4 mt-4">
              <div className="bg-white p-4 rounded-lg">
                <p className="text-gray-800 text-sm mb-3">Scan this QR code with your authenticator app:</p>
                <img src={qrCode} alt="2FA QR Code" className="mx-auto" />
                <p className="text-xs text-gray-600 mt-2 text-center">Or enter this code manually: <code className="bg-gray-100 px-2 py-1 rounded">{secret}</code></p>
              </div>

              <div>
                <Label className="text-white">Backup Codes (Save these in a safe place)</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {backupCodes.map((code) => (
                    <div key={code} className="bg-gray-900 p-2 rounded flex items-center justify-between">
                      <code className="text-green-400 text-sm">{code}</code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(code)}
                        className="text-gray-400 hover:text-white"
                        aria-label={`Copy backup code ${code}`}
                      >
                        <Copy className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-white">Enter code from your app to verify:</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                  <Button
                    onClick={handleEnable2FA}
                    disabled={loading || verificationCode.length !== 6}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Verify & Enable
                  </Button>
                </div>
              </div>
            </div>
          )}

          {twoFactorEnabled && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-2 text-green-400 mb-3">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">2FA is enabled on your account</span>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                Your account is protected with two-factor authentication.
              </p>
              <div>
                <Label className="text-white text-sm">Enter your 2FA code to disable:</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter code"
                    maxLength={6}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                  <Button
                    onClick={handleDisable2FA}
                    disabled={loading}
                    variant="destructive"
                  >
                    Disable 2FA
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Management */}
      {user && user.email && user.email.includes('@igotsoul.temp') && (
        <Card className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-yellow-400 font-semibold mb-1">⚠️ Add Your Email</h3>
                <p className="text-gray-400 text-sm">
                  You registered with phone only. Add an email for better account security.
                </p>
              </div>
              <Button
                onClick={() => window.location.href = '/account-settings?tab=profile'}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                Add Email
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phone Number Verification */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold">Phone Number</h3>
              <p className="text-gray-400 text-sm">Add phone for sign-in and recovery</p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-white">Phone Number</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 234 567 8900"
                  className="bg-gray-900 border-gray-700 text-white"
                  disabled={phoneVerified}
                />
                {!phoneVerified && (
                  <Button
                    onClick={handleRequestPhoneVerification}
                    disabled={loading || !phone}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Verify
                  </Button>
                )}
                {phoneVerified && (
                  <div className="flex items-center gap-2 px-3 bg-green-500/20 rounded">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 text-sm">Verified</span>
                  </div>
                )}
              </div>
            </div>

            {showPhoneVerification && (
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                <Label className="text-white">Enter verification code:</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="text"
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                  <Button
                    onClick={handleVerifyPhone}
                    disabled={loading || phoneCode.length !== 6}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Confirm
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Check your phone for the verification code (SMS)
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <Key className="w-6 h-6 text-yellow-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold">Password</h3>
              <p className="text-gray-400 text-sm mb-4">Change your password regularly to keep your account secure</p>
              <Button
                onClick={() => window.location.href = '/forgot-password'}
                variant="outline"
                className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-white"
              >
                Change Password
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettings;
